import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Leaf, Users, Recycle, SealCheck, ShoppingBag, TrendUp } from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import ProductCard from '@/components/custom/ProductCard';
import ArtisanCard from '@/components/custom/ArtisanCard';
import HEROImage from '@/asset/artisan.jpg'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [products, setProducts] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, artisansRes, statsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/artisans`),
          axios.get(`${API}/impact/stats`)
        ]);
        setProducts(productsRes.data.slice(0, 4));
        setArtisans(artisansRes.data.slice(0, 3));
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C3E2D] to-[#4A6C4C]" />
        <div className="absolute inset-0 bg-grain opacity-30" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <Badge variant="secondary" className="mb-6 bg-white/10 text-white border-white/20">
                <Leaf weight="duotone" className="w-4 h-4 mr-2" />
                Sustainable Fashion Revolution
              </Badge>
              <h1 className="text-5xl md:text-7xl font-semibold text-white leading-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Authentic Stories.
                <br />
                <span className="text-white/80">Transparent Journeys.</span>
              </h1>
              <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-lg">
                Discover handcrafted treasures from artisan communities worldwide. 
                Every purchase supports fair trade, preserves cultural heritage, and tracks 
                your sustainable impact.
              </p>
              <div className="flex flex-wrap gap-4">
                {/*<Link to="/products">
                  <Button className="h-12 px-8 rounded-full bg-white text-[#2C3E2D] hover:bg-white/90 font-medium" data-testid="explore-products-btn">
                    Explore Products
                    <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
                  </Button>
                </Link>*/}
                <Link to="/impact">
                  <Button variant="outline" className="h-12 px-8 rounded-full border-white/30 text-white hover:bg-white/10" data-testid="view-impact-btn">
                    View Our Impact
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={HEROImage}
                  alt="Artisan at work"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl animate-fade-in-up stagger-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#4A6C4C]/10 rounded-full flex items-center justify-center">
                    <Users weight="duotone" className="w-6 h-6 text-[#4A6C4C]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_artisans || 0}+</p>
                    <p className="text-sm text-muted-foreground">Artisans Empowered</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-24 bg-secondary/30" data-testid="impact-stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ImpactStat
              icon={<Users weight="duotone" className="w-8 h-8" />}
              value={stats?.total_artisans || 0}
              label="Artisans"
              suffix="+"
            />
            <ImpactStat
              icon={<ShoppingBag weight="duotone" className="w-8 h-8" />}
              value={stats?.total_products || 0}
              label="Products"
              suffix="+"
            />
            <ImpactStat
              icon={<Recycle weight="duotone" className="w-8 h-8" />}
              value={stats?.carbon_saved_kg || 0}
              label="CO₂ Saved"
              suffix="kg"
            />
            <ImpactStat
              icon={<TrendUp weight="duotone" className="w-8 h-8" />}
              value={stats?.fair_trade_percentage || 85}
              label="To Artisans"
              suffix="%"
            />
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <Badge variant="outline" className="mb-4">Featured Collection</Badge>
              <h2 className="text-4xl md:text-5xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                Handcrafted with Purpose
              </h2>
            </div>
            <Link to="/products" className="hidden md:flex items-center gap-2 text-primary hover:gap-3 transition-all">
              View All <ArrowRight weight="bold" className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="aspect-[4/5] bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="mt-8 md:hidden text-center">
            <Link to="/products">
              <Button variant="outline" className="rounded-full">
                View All Products <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-24 bg-[#2C3E2D] text-white overflow-hidden" data-testid="transparency-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge variant="secondary" className="mb-6 bg-white/10 border-white/20">
                <SealCheck weight="duotone" className="w-4 h-4 mr-2" />
                Blockchain Verified
              </Badge>
              <h2 className="text-4xl md:text-5xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Complete Transparency,
                <br />
                <span className="text-white/70">Every Purchase</span>
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                Every product comes with a digital passport powered by blockchain technology. 
                Track the complete journey from artisan to your doorstep, including sustainability 
                metrics, fair trade verification, and environmental impact.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <TransparencyFeature icon={<SealCheck />} title="Verified Origin" desc="Artisan authenticated" />
                <TransparencyFeature icon={<Recycle />} title="Eco Impact" desc="Carbon footprint tracked" />
                <TransparencyFeature icon={<Users />} title="Fair Trade" desc="85% goes to artisans" />
                <TransparencyFeature icon={<Leaf />} title="Sustainable" desc="Certified materials" />
              </div>
            </div>
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-4 border-dashed border-white/40 mb-4">
                    <SealCheck weight="duotone" className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold">Digital Product Passport</h3>
                  <p className="text-white/60 text-sm mt-1">Blockchain ID: 0x7a3f...8e2d</p>
                </div>
                <div className="space-y-4">
                  <PassportRow label="Artisan" value="Lakshmi Devi, Varanasi" />
                  <PassportRow label="Material" value="100% Pure Silk" />
                  <PassportRow label="Technique" value="Handloom Weaving" />
                  <PassportRow label="Crafting Time" value="48 hours" />
                  <PassportRow label="Carbon Saved" value="1.2 kg CO₂e" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Artisans Section */}
      <section className="py-24" data-testid="artisans-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Meet the Makers</Badge>
            <h2 className="text-4xl md:text-5xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Stories Behind the Craft
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect with artisan communities preserving centuries-old traditions 
              while building sustainable livelihoods.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1,2,3].map(i => (
                <div key={i} className="aspect-[4/5] bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {artisans.map(artisan => (
                <ArtisanCard key={artisan.id} artisan={artisan} />
              ))}
            </div>
          )}

          <div className="mt-12 text-center">
            <Link to="/artisans">
              <Button variant="outline" className="rounded-full">
                Meet All Artisans <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Join the Sustainable Fashion Movement
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Every purchase creates impact. Track your contribution to artisan empowerment, 
            cultural preservation, and environmental sustainability.
          </p>
          <Link to="/products">
            <Button className="h-14 px-10 rounded-full bg-primary text-primary-foreground font-medium text-lg" data-testid="shop-now-btn">
              Shop Now
              <ArrowRight weight="bold" className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Helper Components
const ImpactStat = ({ icon, value, label, suffix }) => (
  <div className="impact-stat text-center">
    <div className="inline-flex items-center justify-center w-14 h-14 bg-[#4A6C4C]/10 rounded-full mb-4 text-[#4A6C4C]">
      {icon}
    </div>
    <p className="text-3xl md:text-4xl font-bold text-foreground mb-1">
      {value}{suffix}
    </p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

const TransparencyFeature = ({ icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
      <span className="w-5 h-5">{icon}</span>
    </div>
    <div>
      <h4 className="font-medium">{title}</h4>
      <p className="text-sm text-white/60">{desc}</p>
    </div>
  </div>
);

const PassportRow = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-white/10 last:border-0">
    <span className="text-white/60 text-sm">{label}</span>
    <span className="text-sm font-medium">{value}</span>
  </div>
);

export default LandingPage;
